<script setup lang="ts">
import { computed } from 'vue'

import type { CreatorExtraInfo } from '@/lib/types/creator-detail.types'
import { BoxComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  extraInfo: CreatorExtraInfo | null
}>()

const bankType = computed(() => props.extraInfo?.bankType ?? null)
const bankCountry = computed(() => props.extraInfo?.bankInfo.bankCountry ?? '')

const PAYMENT_METHOD_LABELS: Record<CreatorExtraInfo['bankType'], string> = {
  CLEARING: 'Clearing',
  SORT: 'Sort code',
  IBAN: 'IBAN',
}

const paymentMethodLabel = computed(() => (bankType.value ? PAYMENT_METHOD_LABELS[bankType.value] : '-'))

const bankCodeLabel = computed(() => {
  const country = bankCountry.value
  if (bankType.value === 'SORT') {
    return 'Sort code'
  }
  if (country === 'AU') {
    return 'BSB number'
  }
  if (country === 'US') {
    return 'Routing number'
  }
  if (country === 'SE' && bankType.value === 'CLEARING') {
    return 'Clearing number'
  }
  return 'Bank code'
})

const showIbanFields = computed(() => bankType.value === 'IBAN')
const showClearingFields = computed(() => bankType.value === 'CLEARING' || bankType.value === 'SORT')
</script>
<template>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Payment method</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ paymentMethodLabel }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Bank name</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">
        {{ extraInfo?.bankInfo.bankName || '-' }}
      </TypographyComponent>
    </BoxComponent>
  </FlexComponent>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Bank country</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">
        {{ extraInfo?.bankInfo.bankCountry || '-' }}
      </TypographyComponent>
    </BoxComponent>
  </FlexComponent>
  <template v-if="showIbanFields">
    <FlexComponent align="center" class="info-field">
      <BoxComponent class="info-field__content">
        <TypographyComponent variant="label-large">IBAN</TypographyComponent>
        <TypographyComponent variant="body-medium" color="var(--secondary-48)">
          {{ extraInfo?.bankInfo.iban || '-' }}
        </TypographyComponent>
      </BoxComponent>
    </FlexComponent>
    <FlexComponent align="center" class="info-field">
      <BoxComponent class="info-field__content">
        <TypographyComponent variant="label-large">BIC/SWIFT</TypographyComponent>
        <TypographyComponent variant="body-medium" color="var(--secondary-48)">
          {{ extraInfo?.bankInfo.bic || '-' }}
        </TypographyComponent>
      </BoxComponent>
    </FlexComponent>
  </template>
  <template v-if="showClearingFields">
    <FlexComponent align="center" class="info-field">
      <BoxComponent class="info-field__content">
        <TypographyComponent variant="label-large">{{ bankCodeLabel }}</TypographyComponent>
        <TypographyComponent variant="body-medium" color="var(--secondary-48)">
          {{ extraInfo?.bankInfo.bankCode || '-' }}
        </TypographyComponent>
      </BoxComponent>
    </FlexComponent>
    <FlexComponent align="center" class="info-field">
      <BoxComponent class="info-field__content">
        <TypographyComponent variant="label-large">Account number</TypographyComponent>
        <TypographyComponent variant="body-medium" color="var(--secondary-48)">
          {{ extraInfo?.bankInfo.accountNumber || '-' }}
        </TypographyComponent>
      </BoxComponent>
    </FlexComponent>
  </template>
</template>
<style scoped lang="scss">
.info-field {
  padding: 16px 32px;
  border-bottom: 1px solid var(--secondary-4);
  align-items: center;
  &:last-child {
    border-bottom: none;
  }
}
.info-field__content {
  flex: 1;
  min-width: 0;
}
</style>
